import { Attribute, Relationship, Resource } from 'json-api';
import { Translatable } from 'src/app/translation/translatable';
import { AtomResource } from '../classes/atom-resource';
import { Validable } from '../common-behaviors/validate.mixin';
import { WithMessages } from '../common-behaviors/with-messages.mixin';
import { BusinessUnit } from './business-unit.resource';
import { ContentRate } from './content-rate.resource';
import { Country } from './country.resource';
import { CredentialsSetup } from './credentials-setup.resource';
import { CreditSetup } from './credit-setup.resource';
import { Firm } from './firm.resource';
import { Offer } from './offer.resource';
import { Platform } from './platform.resource';
import { Service } from './service.resource';
import { Thematic } from './thematic.resource';
import { Trademark } from './trademark.resource';
import { AcquisitionAccounts } from './acquisition-account.resource';
import { Notificationmanagementsdvp4m } from './notification.resource';
import { Keywordrulesdvp4m } from './keywordrulesdvp4m.ressource';
import { ResourceConfig } from 'src/app/resources/resources-interfaces/resource-config';
import { AtomService } from '../atom.service';
import {
  NumberFilter,
  ResourceListFilter,
  StringFilter,
} from 'src/app/filter/filter';
import { Message, MessageConfig } from './message.resource';
import {
  BusinessunitConfig,
  CountryConfig,
  FirmConfig,
  PlatformConfig,
  ServiceConfig,
  ThematicConfig,
  TrademarksConfig,
  LegalDocumentConfig as LegalDocumentConfigSearch
} from 'src/app/shared/tools/search/search-config';
import { LegalDocument, LegalDocumentConfig } from './legal-document.resource';

const ProductMixinBase = WithMessages(Validable(AtomResource));
@Resource({
  type: 'products',
})
export class Product extends AtomResource {
  @Attribute() name?: Translatable;
  @Attribute({ key: 'commercial_name' }) commercialName?: Translatable;
  @Attribute({ key: 'phone_support' }) phoneSupport?: string;
  @Attribute({ key: 'email_support' }) emailSupport?: string;
  @Attribute({ key: 'homepage_url' }) homepageUrl?: string;
  @Attribute({ key: 'welcomepage_url' }) welcomepageUrl?: string;
  @Attribute({ key: 'termspage_url' }) termspageUrl?: string;
  @Attribute({ key: 'authpage_url' }) authpageUrl?: string;
  @Attribute({ key: 'landingpage_url' }) landingpageUrl?: string;
  @Attribute({ key: 'selfcare_url' }) selfcareUrl?: string;
  @Attribute({ key: 'with_credit' }) withCredit?: string;
  @Attribute({ key: 'snowplow_application_id' }) snowplowId?: string;
  @Attribute({ key: 'didomi_consent_id' }) didomiConsentId?: string;
  @Attribute({ key: 'validity_tz' }) validityTz?: string;
  @Attribute({ key: 'personal_data_url' }) personalDataUrl?: string;
  @Attribute() validity?: string;
  @Attribute() data?: Record<string, unknown>;

  @Relationship() businessunits?: BusinessUnit[];
  @Relationship() contentrate?: ContentRate;
  @Relationship() country?: Country;
  @Relationship() handlerfirm?: Firm;
  @Relationship() legalfirm?: Firm;
  @Relationship() service?: Service;
  @Relationship() offers?: Offer[];
  @Relationship() thematic?: Thematic;
  @Relationship() creditsetup?: CreditSetup;
  @Relationship() credentialssetup?: CredentialsSetup;
  @Relationship() eventplatform?: Platform;
  @Relationship() trademark?: Trademark;
  @Relationship() acquisitionaccounts?: AcquisitionAccounts[];
  @Relationship() notificationmanagementsdvp4m?: Notificationmanagementsdvp4m[];
  @Relationship() keywordrulesdvp4m?: Keywordrulesdvp4m[];
  @Relationship() messages?: Message[];
  @Relationship() legaldocuments?: LegalDocument[];

}
export class ProductConfig implements ResourceConfig {
  tables = {
    includes: ['country.dvcontentextendedcountry', 'service', 'thematic'],
    columns: [
      {
        columnDef: 'id',
        displayedDatas: [{ path: ['id'], class: 'main' }],
        header: 'ID',
        translatable: false,
        displayed: true,
        sort: 'id',
      },
      {
        columnDef: 'status',
        header: '',
        displayed: true,
      },
      {
        columnDef: 'name',
        displayedDatas: [
          {
            path: ['name'],
            class: 'mat-body-2',
          },
          {
            path: ['commercialName'],
            class: 'mat-body-2',
          },
        ],
        header: 'Name / Commercial Name',
        displayed: true,
        link: {
          detail: true,
        },
        sort: 'name',
      },
      {
        columnDef: 'country',
        displayedDatas: [
          {
            path: ['country.name'],
            class: 'mat-body-2',
            translatable: true,
          },
          {
            path: ['country.id'],
            class: 'mat-body-1',
            translatable: false,
          },
        ],
        header: 'Country',
        translatable: true,
        displayed: true,
        link: {
          resource: 'countries',
          filter: 'country.id',
          icon: 'language',
        },
        options: {
          flag: 'country.id',
        },
        sort: 'country.name',
      },
      {
        columnDef: 'thematic',
        displayedDatas: [
          {
            path: ['thematic.name'],
            class: 'mat-body-2',
          },
        ],
        header: 'Thematic',
        translatable: true,
        displayed: true,
        sort: 'thematic.name',
      },
      {
        columnDef: 'service',
        displayedDatas: [
          {
            path: ['service.name'],
            class: 'mat-body-2',
          },
        ],
        header: 'Service',
        translatable: true,
        displayed: true,
        link: {
          resource: 'services',
          filter: 'service.id',
          icon: 'shopping_basket',
        },
        sort: 'service.name',
      },
      {
        columnDef: 'offers',
        header: 'Offers',
        displayed: true,
        link: {
          resource: 'offers',
          list: 'offers',
          target: 'product',
        },
      },
      {
        columnDef: 'actions',
        header: 'Actions',
        displayed: true,
      },
    ],
  };
  detail = {
    header: {
      icon: 'shopping_basket',
      options: {
        flag: 'country.id',
      },
      title: [
        {
          path: ['name'],
          translatable: false,
        },
      ],
      subtitle: [
        {
          path: ['id', 'country.name', 'country.id'],
          translatable: false,
        },
      ],
      validity: true,
    },
    elements: [
      {
        columns: true,
        showEmpty: true,
        list: [
          {
            icon: 'badge',
            title: 'Commercial Name',
            APIKey: 'commercialName',
            value: {
              path: ['commercialName'],
            },
          },
          {
            icon: 'public',
            title: 'Service',
            APIKey: 'service',
            value: {
              path: ['service.name'],
            },
            link: {
              resource: 'services',
              filter: 'service.id',
            },
          },
          {
            icon: 'group_work',
            title: 'Thematic',
            APIKey: 'thematic',
            value: {
              path: ['thematic.name'],
            },
          },
          {
            icon: 'credit_card',
            title: 'With Credit ?',
            APIKey: 'withCredit',
            value: {
              path: ['withCredit'],
              type: 'boolean',
            },
          },
          {
            icon: 'category',
            title: 'Business Unit',
            APIKey: 'businessunits',
            value: {
              path: ['name'],
              type: 'chips',
            },
          },
          {
            icon: 'local_offer',
            title: 'Offers',
            APIKey: 'offers',
            value: {
              path: ['offers'],
            },
            link: {
              resource: 'offers',
              list: 'offers',
              target: 'product',
            },
          },
        ],
      }
    ],
    tabs: [
      {
        title: 'Firms',
        elements: [
          {
            showEmpty: true,
            list: [
              {
                icon: 'business',
                title: 'Handler Firm',
                APIKey: 'handlerfirm',
                value: {
                  path: ['handlerfirm.name'],
                },
                link: {
                  resource: 'firms',
                  filter: 'handlerfirm.id',
                },
              },
              {
                icon: 'business',
                title: 'Legal Firm',
                APIKey: 'legalfirm',
                value: {
                  path: ['legalfirm.name'],
                },
                link: {
                  resource: 'firms',
                  filter: 'legalfirm.id',
                },
              },
              {
                icon: 'copyright',
                title: 'Trademark',
                APIKey: 'trademark',
                value: {
                  path: ['trademark.officialName'],
                },
              },
              {
                icon: 'dns',
                title: 'Event Platform',
                APIKey: 'eventplatform',
                value: {
                  path: ['eventplatform.name'],
                },
                link: {
                  resource: 'platforms',
                  filter: 'eventplatform.id',
                },
              },
            ],
          },
        ],
      },
      {
        title: 'Credential Setup',
        elements: [
          {
            list: [
              {
                type: 'credential',
              },
            ],
          },
        ],
      },
      {
        title: 'Messages',
        elements: [
          {
            resource: {
              APIKey: 'messages',
              config: new MessageConfig(this.atom),
              multiple: true,
            },
          },
        ],
      },
      {
        title: 'Trackers, URLS & Contacts',
        elements: [
          {
            title: 'Trackers',
            columns: true,
            showEmpty: true,
            list: [
              {
                icon: 'numbers',
                title: 'Didiomi ID',
                APIKey: 'didomiConsentId',
                value: {
                  path: ['didomiConsentId'],
                },
                options: {
                  copy: true,
                },
              },
              {
                icon: 'snowflake',
                title: 'Snowplow ID',
                APIKey: 'snowplowId',
                value: {
                  path: ['snowplowId'],
                },
                options: {
                  copy: true,
                },
              },
            ],
          },
          {
            title: 'URL',
            showEmpty: true,
            columns: true,
            list: [
              {
                icon: 'home',
                title: 'Home Page',
                APIKey: 'homepageUrl',
                value: {
                  path: ['homepageUrl'],
                },
                link: {
                  external: true,
                  href: 'homepageUrl',
                },
              },
              {
                icon: 'http',
                title: 'Welcome Page',
                APIKey: 'welcomepageUrl',
                value: {
                  path: ['welcomepageUrl'],
                },
                link: {
                  external: true,
                  href: 'welcomepageUrl',
                },
              },
              {
                icon: 'login',
                title: 'Auth Page',
                APIKey: 'authpageUrl',
                value: {
                  path: ['authpageUrl'],
                },
                link: {
                  external: true,
                  href: 'authpageUrl',
                },
              },
              {
                icon: 'http',
                title: 'Landing Page',
                APIKey: 'landingpageUrl',
                value: {
                  path: ['landingpageUrl'],
                },
                link: {
                  external: true,
                  href: 'landingpageUrl',
                },
              },
              {
                icon: 'gavel',
                title: 'Terms and Conditions Page',
                APIKey: 'termspageUrl',
                value: {
                  path: ['termspageUrl'],
                },
                link: {
                  external: true,
                  href: 'termspageUrl',
                },
              },
              {
                icon: 'support_agent',
                title: 'Selfcare Page',
                APIKey: 'selfcareUrl',
                value: {
                  path: ['selfcareUrl'],
                },
                link: {
                  external: true,
                  href: 'selfcareUrl',
                },
              },
              {
                icon: 'open_in_new',
                title: 'Personal Data Url',
                APIKey: 'personalDataUrl',
                value: {
                  path: ['personalDataUrl'],
                },
                link: {
                  external: true,
                  href: 'personalDataUrl',
                },
              },
            ],
          },
          {
            title: 'Contacts',
            columns: true,
            showEmpty: true,
            list: [
              {
                icon: 'phone',
                title: 'Phone Support',
                APIKey: 'phoneSupport',
                value: {
                  path: ['phoneSupport'],
                },
              },
              {
                icon: 'email',
                title: 'Email Support',
                APIKey: 'emailSupport',
                value: {
                  path: ['emailSupport'],
                },
              },
            ],
          },
        ],
      },
      // {
      //   title: 'Documents / Data',
      //   elements: [
      //     {
      //       showEmpty: true,
      //       list: [
      //         {
      //           icon: 'local_police',
      //           title: 'Legal Documents',
      //           type: 'chips',
      //           APIKey: 'legaldocuments',
      //           value: {
      //             path: ['name','documentType'],
      //           },
      //           link: {
      //             resource: 'legaldocuments',
      //             filter: 'legaldocument.id',
      //           },
      //         },
      //         {
      //           title: 'Data',
      //           type: 'data',
      //           APIKey: 'data',
      //           value: {
      //             path: ['data'],
      //           },
      //         },
      //       ],
      //     },
      //   ],
      // },
    ],
  };
  wizard = {
    tabs: [
      {
        title: 'General Informations',
        formGroup: 'general',
        list: [
          {
            type: 'text',
            title: 'Name',
            APIKey: 'name',
            formControl: 'name',
            required: true,
            translatable: true,
          },
          {
            type: 'text',
            title: 'Commerical Name',
            formControl: 'commercialName',
            required: true,
            translatable: true,
          },
          {
            type: 'resource',
            title: 'Service',
            formControl: 'service',
            APIKey: 'service',
            search: new ServiceConfig(this.atom),
            required: true,
            activatingSequence: [
              {
                group: 'general',
                control: 'businessunits',
                type: 'resource',
                inherit: true,
              },
            ],
          },
          {
            type: 'resource',
            title: 'Business Units',
            formControl: 'businessunits',
            APIKey: 'businessunits',
            search: new BusinessunitConfig(true),
            multiple: true,
            required: false,
            inherit: {
              resourceKey: 'businessunits',
              from: 'businessunits',
              path: 'name',
            },
          },
          {
            type: 'resource',
            title: 'Thematic',
            formControl: 'thematic',
            APIKey: 'thematic',
            search: new ThematicConfig(false),
            multiple: false,
            required: false,
          },
          {
            type: 'resource',
            title: 'Country',
            formControl: 'country',
            APIKey: 'country',
            search: new CountryConfig(this.atom),
            required: true,
          },
          {
            type: 'boolean',
            title: 'With Credit ?',
            formControl: 'withCredit',
            noCols: true,
          }
        ],
      },
      {
        title: 'Firms',
        formGroup: 'firms',
        list: [
          {
            type: 'resource',
            title: 'Handler Firm',
            formControl: 'handlerfirm',
            APIKey: 'handlerfirm',
            search: new FirmConfig(this.atom),
            required: true,
            noCols: true,
          },
          {
            type: 'resource',
            title: 'Legal Firm',
            formControl: 'legalfirm',
            APIKey: 'legalfirm',
            search: new FirmConfig(this.atom),
            required: true,
            noCols: true,
            activatingSequence: [
              {
                group: 'firms',
                control: 'trademark',
                filters: {
                  filter: 'equal.firm.id',
                },
              },
              {
                group: 'trackers',
                control: 'didomiConsentId',
                type: 'resource',
                inherit: true,
              },
            ],
          },
          {
            type: 'resource',
            title: 'Trademark',
            formControl: 'trademark',
            APIKey: 'trademark',
            search: new TrademarksConfig(),
            noCols: true,
            // activatingSequence: [s
            //   {
            //     group: 'trackers',
            //     control: 'didomiConsentId',
            //     type: 'resource',
            //     inherit: true,
            //   },
            // ],
          },
          {
            type: 'resource',
            title: 'Event Platform',
            formControl: 'eventplatform',
            APIKey: 'eventplatform',
            search: new PlatformConfig(this.atom),
            noCols: true,
          },
        ],
      },
      {
        title: 'Credentials Setup',
        formGroup: 'credentials',
        list: [
          {
            type: 'credentials',
            title: 'Credentials Setup',
            formControl: 'credentialssetup',
            noCols: true,
          },
        ],
      },
      {
        title: 'URLS',
        formGroup: 'urls',
        list: [
          {
            type: 'text',
            title: 'Home Page',
            APIKey: 'homepageUrl',
            formControl: 'homepageUrl',
          },
          {
            type: 'text',
            title: 'Welcome Page',
            APIKey: 'welcomepageUrl',
            formControl: 'welcomepageUrl',
          },
          {
            type: 'text',
            title: 'Auth Page',
            APIKey: 'authpageUrl',
            formControl: 'authpageUrl',
          },
          {
            type: 'text',
            title: 'Landing Page',
            APIKey: 'landingpageUrl',
            formControl: 'landingpageUrl',
          },
          {
            type: 'text',
            title: 'Terms and Conditions Page',
            APIKey: 'termspageUrl',
            formControl: 'termspageUrl',
          },
          {
            type: 'text',
            title: 'Selfcare Page',
            APIKey: 'selfcareUrl',
            formControl: 'selfcareUrl'

          },
          {
            type: 'text',
            title: 'Personal Data',
            APIKey: 'personal_data_url',
            formControl: 'personalDataUrl',
            hint: 'Max 150 characters',
          },
        ],
      },
      {
        title: 'Trackers & Contacts',
        formGroup: 'trackers',
        list: [
          {
            type: 'inherit',
            title: 'Didomi ID',
            APIKey: 'didomiConsentId',
            formControl: 'didomiConsentId',
            inheritElements: [
              {
                resourceKey: 'legalfirm',
                title: 'Legal Firm',
                path: 'didomiConsentId',
              },
              {
                resourceKey: 'trademark',
                title: 'Trademark',
                path: 'didomiConsentId',
              },
            ],
            noCols: true,
          },
          {
            type: 'text',
            title: 'Snowplow ID',
            APIKey: 'snowplowId',
            formControl: 'snowplowId',
          },
          {
            type: 'text',
            title: 'Phone Support',
            APIKey: 'phoneSupport',
            formControl: 'phoneSupport',
          },
          {
            type: 'text',
            title: 'Email Support',
            APIKey: 'emailSupport',
            formControl: 'emailSupport',
          },
        ],
      },
      {
        title: 'Docu - Data - Validity',
        formGroup: 'data',
        list: [
          // {
          //   type: 'resource',
          //   title: 'Legal Documents',
          //   formControl: 'legaldocuments',
          //   APIKey: 'legaldocuments',
          //   search: new LegalDocumentConfigSearch(this.atom, true),
          //   noCols: true,
          // },
          {
            type: 'data',
            formControl: 'data',
            title: 'data',
            required: false,
          },
          {
            type: 'validity',
            title: 'Validity',
            formControl: 'validity',
            required: true,
          },
        ],
      },
    ],
  };
  resource = Product;
  resourceName = 'Product';
  resourceKey = 'products';
  resourceKeySingle = 'product';
  includes = [
    'country.dvcontentextendedcountry',
    'service.businessunits',
    'offers',
    'thematic',
    'creditsetup',
    'credentialssetup.enumlogingenerationmode',
    'credentialssetup.enumloginusedictionnary',
    'credentialssetup.enumpasswordgenerationmode',
    'credentialssetup.enumpasswordusedictionnary',
    'businessunits',
    'handlerfirm.headquarterscountry',
    'legalfirm.headquarterscountry',
    'legalfirm.trademarks',
    'messages.platform.job.firm',
    'messages.enumtype',
    'messages.tags',
    'eventplatform',
    'trademark',
    // 'legaldocuments'
  ];
  filters = [
    new NumberFilter('id', 'ID'),
    new StringFilter('name', 'Name'),
    new StringFilter('country.id', 'Country ID', true),
    new StringFilter('country.name', 'Country name'),
    new NumberFilter('service.id', 'Service ID'),
    new StringFilter('service.name', 'Service name'),
    new StringFilter('contentrate.name', 'Content Rate'),
    new ResourceListFilter(this.atom, Thematic, 'thematic.id', 'Thematic'),
    new ResourceListFilter(
      this.atom,
      BusinessUnit,
      'businessunits.id',
      'Business unit'
    ),
    new NumberFilter('handlerfirm.id', 'Handler firm ID'),
    new StringFilter('handlerfirm.name', 'Handler firm name'),
    new NumberFilter('legalfirm.id', 'Legal firm ID'),
    new StringFilter('legalfirm.name', 'Legal firm name'),
    new StringFilter('eventplatform.name', 'Event Platform'),
    new NumberFilter('service.ownerfirm.id', 'Owner firm ID'),
    new StringFilter('service.ownerfirm.name', 'Owner firm name'),
    new NumberFilter('trademark.id', 'Trademark ID'),
    new StringFilter('trademark.name', 'Trademark name'),
    // new NumberFilter('legaldocuments.id', 'Legal Document ID'),
    // new StringFilter('legaldocuments.name', 'Legal Document name'),
  ];
  constructor(private atom: AtomService) {}
}
